import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogActions, Typography, Button, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import useTeamColors from '../../hooks/useTeamColors';

/**
 * Online equivalent of RoundWinnerModal.jsx — same visual language (trophy,
 * team-colored glow, winning players' avatars), but driven by server state
 * (a ROUND_TRANSITION broadcast) instead of a local ScoreMarker.onWin
 * callback, and requiring every connected human seat to click "Continuar"
 * (with `ackDeadlineAt` as a countdown fallback so one AFK player can't
 * block the match — matchTransition.ts's ack/timeout race).
 */
export default function RoundTransitionModal({ open, winnerTeamId, matchScore, nextGameName, playersBySeat, hasAcked, ackDeadlineAt, onContinue, readOnly = false }) {
    const { t } = useTranslation();
    const { teamColors } = useTeamColors();
    const [secondsLeft, setSecondsLeft] = useState(null);

    useEffect(() => {
        if (!open || !ackDeadlineAt) {
            setSecondsLeft(null);
            return;
        }
        const tick = () => setSecondsLeft(Math.max(0, Math.ceil((ackDeadlineAt - Date.now()) / 1000)));
        tick();
        const interval = setInterval(tick, 250);
        return () => clearInterval(interval);
    }, [open, ackDeadlineAt]);

    if (!winnerTeamId) return null;

    const winningSeats = winnerTeamId === 1 ? [0, 2] : [1, 3];
    const winnerColorHex = winnerTeamId === 1 ? teamColors.team1.main : teamColors.team2.main;

    return (
        <Dialog
            open={open}
            disableEscapeKeyDown
            PaperProps={{
                sx: {
                    background: 'linear-gradient(145deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
                    borderRadius: '20px',
                    border: `3px solid ${winnerColorHex}`,
                    boxShadow: `0 0 30px ${winnerColorHex}40, 0 10px 40px rgba(0,0,0,0.5)`,
                    maxWidth: '420px',
                    width: '90%',
                    overflow: 'hidden',
                },
            }}
        >
            <Box sx={{ height: '6px', background: `linear-gradient(90deg, ${winnerColorHex} 0%, ${winnerColorHex}80 50%, ${winnerColorHex} 100%)` }} />

            <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 2, padding: '28px 24px 20px' }}>
                <EmojiEventsIcon sx={{ fontSize: 64, color: '#FFD700', filter: 'drop-shadow(0 0 15px rgba(255, 215, 0, 0.6))' }} />

                <Typography variant="h5" sx={{ color: '#ffffff', fontWeight: 'bold', textShadow: `0 0 20px ${winnerColorHex}` }}>
                    {t('online.play.round_transition.title', { teamId: winnerTeamId })}
                </Typography>

                <Box sx={{ display: 'flex', gap: 4, mt: 1 }}>
                    {winningSeats.map((seat) => {
                        const player = playersBySeat?.[seat];
                        if (!player) return null;
                        return (
                            <Box key={seat} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                <Box
                                    sx={{
                                        width: 60,
                                        height: 60,
                                        borderRadius: '50%',
                                        border: `3px solid ${winnerColorHex}`,
                                        boxShadow: `0 0 20px ${winnerColorHex}60`,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '1.8rem',
                                        backgroundColor: 'rgba(255, 255, 255, 0.08)',
                                    }}
                                >
                                    {player.avatar || (player.isSystem ? '🤖' : '🙂')}
                                </Box>
                                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.95)', mt: 1, fontWeight: 600 }}>
                                    {player.name}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>

                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)' }}>
                    {t('online.play.team', { team: 1 })} {matchScore?.[1] ?? 0} — {matchScore?.[2] ?? 0} {t('online.play.team', { team: 2 })}
                </Typography>

                {nextGameName && (
                    <Box sx={{ px: 3, py: 1, borderRadius: '12px', backgroundColor: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)' }}>
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 500 }}>
                            {t('round.next_game', { nextGameName })}
                        </Typography>
                    </Box>
                )}

                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                    {readOnly || hasAcked ? t('online.play.round_transition.waiting') : t('online.play.round_transition.auto_continue', { seconds: secondsLeft ?? '…' })}
                </Typography>
            </DialogContent>

            {/* A spectator has no seat to acknowledge for — the round simply continues once the players do (or the deadline passes). */}
            {!readOnly && (
                <DialogActions sx={{ pb: 3, pt: 0, justifyContent: 'center' }}>
                    <Button
                        variant="contained"
                        size="large"
                        disabled={hasAcked}
                        onClick={onContinue}
                        sx={{
                            background: `linear-gradient(135deg, ${winnerColorHex} 0%, ${winnerColorHex}CC 100%)`,
                            color: 'white',
                            fontWeight: 'bold',
                            padding: '12px 48px',
                            borderRadius: '30px',
                            textTransform: 'none',
                        }}
                    >
                        {t('round.continue')}
                    </Button>
                </DialogActions>
            )}
        </Dialog>
    );
}
